import type { Breadcrumb as SentryBreadcrumb, ErrorEvent, Primitive, User } from '@sentry/types';
import consts from '../../config/consts';
import Sentry from './sentry';
import { renderTraceId } from './render-trace';
import { BufferingAbortedError, RenderTaskDroppedError } from '../errors';

export enum Environment {
    Rendering = 'rendering',
    VideoDataServer = 'video data server',
}

type ContextData = Record<string, unknown>;
export type Context = {
    level?: 'error' | 'warning' | 'log';
    extra?: ContextData;
};

type Breadcrumb = SentryBreadcrumb;

export type LuminaryErrorReporterType = typeof LuminaryErrorReporter;

declare global {
    interface Window {
        /**
         * ErrorReporter exposed by the web repo so luminary can use it
         */
        ErrorReporter: LuminaryErrorReporterType;
    }
}

/**
 * A generic class for reporting errors to a backend service.
 * We should try to keep this generic and define our own types so that if we want to
 * swap or add another error reporting service, we can do so easily (without changing a lot of other code).
 *
 * Note that this class' interface should match the ErrorReporter interface in the web repo.
 */
class LuminaryErrorReporter {
    static init(environment: Environment) {
        const DEBUG = !process.env.DEBUG || process.env.DEBUG.toLowerCase() !== 'false';
        Sentry.init({
            dsn: DEBUG ? undefined : consts.SENTRY_DSN,
            environment: environment,
            release: process.env.COMMIT_SHA,
            // We collect our own traces and send them to Sentry from our OTEL collector
            // so we currently don't use sentry's tracing (and thus can't use their profiling)

            // Override only the trace_id for render events so they group into one Sentry
            // trace (see render-trace); each event keeps its own span_id.
            beforeSend: (event: ErrorEvent) => {
                const videoRenderId = event.tags?.video_render_id;
                if (videoRenderId != null && event.contexts?.trace) {
                    event.contexts.trace.trace_id = renderTraceId(String(videoRenderId));
                }
                return event;
            },
        });
    }

    static setUser(user: User) {
        Sentry.setUser(user);
    }

    static capture(error: unknown, context?: Context) {
        context = context || {};
        if (!context.level) {
            context.level = 'error';
        }
        if (typeof error === 'string') {
            Sentry.captureMessage(error, context);
        } else {
            Sentry.captureException(error, context);
        }
    }

    static addBreadcrumb(breadcrumb: Breadcrumb) {
        Sentry.addBreadcrumb(breadcrumb);
    }

    static setContext(name: string, context: ContextData) {
        Sentry.setContext(name, context);
    }

    static setTag(name: string, value: Primitive) {
        Sentry.setTag(name, value);
    }
}

/**
 * Errors that mean "we gave up on this work", not "this work went wrong". They are raised on
 * promises callers are still awaiting, so they have to be thrown, but reporting them would bury
 * real failures under routine cancellation noise.
 */
const isCancellation = (error: unknown) =>
    error instanceof RenderTaskDroppedError || error instanceof BufferingAbortedError;

// If luminary is running in web, use the ErrorReporter from the window object
export const ErrorReporter = new Proxy({} as LuminaryErrorReporterType, {
    get(_target, prop: keyof LuminaryErrorReporterType) {
        const reporter =
            typeof window !== 'undefined' && window.ErrorReporter
                ? window.ErrorReporter
                : LuminaryErrorReporter;

        if (prop === 'capture') {
            return (error: unknown, context?: Context) => {
                if (isCancellation(error)) {
                    return;
                }
                reporter.capture(error, context);
            };
        }

        return reporter[prop];
    },
});
